import { useDispatch, useSelector } from "react-redux";

import { API_BASE_URL, API_ENDPOINTS } from "../constants/api";
import Http from "../services/http";
import {
  actionSetCancelRequestPending,
  actionSetGlobalSiteLinkPopupClose,
  actionSetHasMoreHistory,
  actionSetHistoryMoreLoading,
  actionSetHistoryReqBody,
  actionSetMyDataLoadMoreHistory,
  actionSetRequestDetails,
} from "../store/actions/mydata-actions";
import { actionSetRequestDetailsLoading } from "../store/actions/mydata-actions";
import { actionSetMyDataHistory } from "../store/actions/mydata-actions";
import { actionSetMyDataHistoryLoading } from "../store/actions/mydata-actions";
import { actionSetCancelRequestComplete } from "../store/actions/mydata-actions";
import { actionSetFilePasswordSend } from "../store/actions/mydata-actions";
import {
  actionSetSummary,
  actionSetSummaryLoading,
  actionSetRequestLoading,
  confirmRequestData,
} from "../store/actions/mydata-actions";
import { BaseEntityRes } from "../types/base";
import {
  HISTORY_REQUEST_TYPE,
  MyDataHistoryEntity,
  MyDataHistoryReqBody,
} from "../types/history";
import {
  SummaryEntityRes,
  RequestEntityRes,
  RequestEntityReq,
} from "../types/mydata";
import { REQUEST_TYPE } from "../types/user";
import { UtilsDateTime } from "../utils/UtilsDateTIme";
import { RequestDetailsEntityRes } from "../types/request-details";
import {
  myDataHistoryDefaults,
  myDataHistoryReqBodyDefaults,
} from "../data/defaults";
import { UtilsRequest } from "../utils/UtilsRequest";
import { RCODES } from "../constants/rcodes";
import { ROUTES } from "../configs/routes";
import triggerGAEvent from "../utils/UtilsTriggerGAEvent";
import { UtilsLocale } from "../utils/UtilsLocale";

import {
  EVENT_CATEGORY_MY_DATA,
  EVENT_CATEGORY_MY_DATA_HISTORY,
  EVENT_MY_DATA_ACTION_REQUEST_CONFIRMATION,
  EVENT_MY_DATA_HISTORY_ACTION_ALL,
  EVENT_MY_DATA_HISTORY_ACTION_DELETION,
  EVENT_MY_DATA_HISTORY_ACTION_DOWNLOAD,
  EVENT_MY_DATA_HISTORY_ACTION_OPTOUT,
  EVENT_ACTION_DETAILS_CANCEL_REQUEST,
  EVENT_ACTION_DETAILS_SEND_FILE_PASSWORD,
  EVENT_CATEGORY_REQUEST_DETAILS,
} from "../constants/analytics";
import { ApplicationState } from "../store";
import { LayoutState } from "../store/reducers/layout-reducer";
import Cookies from "js-cookie";
import { LANGUAGE_TRANS, USER_DATA_NAMESPACE } from "../constants/storage";
import { LocalStorage } from "../services/storage";
import { UserState } from "../store/reducers/user-reducer";

export const useMyDataHooks = () => {
  const dispatch = useDispatch();

  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );

  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const loadSummary = async () => {
    try {
      dispatch(actionSetSummaryLoading());
      // const lsUserData = JSON.parse(
      //   LocalStorage.get(USER_DATA_NAMESPACE) || ""
      // );

      const url = `${API_BASE_URL}${API_ENDPOINTS.SUMMARY}`;
      const res: SummaryEntityRes = await Http.get(url, {
        lang: layoutState.languageTrans,
        sa_cc: UtilsLocale.convertISO3toISO2(userState.userData.countryCode),
        site_cc: layoutState.countryCode,
      });

      dispatch(actionSetSummary(res));
    } catch (e: any) {
      console.error(e);
    }
  };

  const confirmRequest = async (
    services: Array<String>,
    requestType: REQUEST_TYPE,
    confirmToken: String
  ) => {
    try {
      dispatch(actionSetRequestLoading());
      const tz = UtilsDateTime.getLocalTimezone();
      const arrServices: { service_code: String }[] = [];
      let url;
      let language = Cookies.get(LANGUAGE_TRANS) || layoutState.languageTrans;
      const lsUserData = JSON.parse(
        LocalStorage.get(USER_DATA_NAMESPACE) || ""
      );

      services.forEach((service) => {
        arrServices.push({ service_code: service });
      });

      if (
        lsUserData?.countryCode === "USA" &&
        layoutState.countryCode === "US" &&
        language === "en"
      ) {
        language = "en-rUS";
      }

      if (requestType === REQUEST_TYPE.DOWNLOAD) {
        url = `${API_BASE_URL}${API_ENDPOINTS.MYDATA_DOWNLOAD}`;
      } else if (requestType === REQUEST_TYPE.DELETE) {
        url = `${API_BASE_URL}${API_ENDPOINTS.MYDATA_DELETE}`;
      } else {
        url = `${API_BASE_URL}${API_ENDPOINTS.MYDATA_UNSUBSCRIBE}`;
      }

      const data: RequestEntityReq = {
        user_request_date: UtilsDateTime.dateObjectToStringUTC(new Date()),
        request_subtype: "R",
        language,
        country: layoutState.countryCode,
        sa_country: UtilsLocale.convertISO3toISO2(lsUserData?.countryCode),
        service_count: arrServices.length,
        service_list: arrServices,
        tz,
        geoip_country: sessionStorage.getItem("GEO_IP"),
      };
      const extraHeaders = {
        "x-sc-confirm-token": confirmToken,
      };
      let res: RequestEntityRes;
      if (requestType === REQUEST_TYPE.UNSUBSCRIBE) {
        res = await Http.post(url, data, extraHeaders).catch(
          (response: any) => response
        );
      } else {
        res = await Http.post(url, data).catch((response: any) => response);
      }

      if (res.rcode === 0 || res.rcode === RCODES.GK_DUPLICATE_REQUEST) {
        // allow normal flow with gk duplicate request
        dispatch(confirmRequestData(res));
      }

      UtilsRequest.removeRequestInfo();
      triggerGAEvent(
        EVENT_CATEGORY_MY_DATA,
        EVENT_MY_DATA_ACTION_REQUEST_CONFIRMATION
      );
    } catch (e: any) {
      console.error(e);
    }
  };

  const clearConfirmRequestData = () => {
    dispatch(confirmRequestData({ request_number: "" }));
  };

  const loadRequestDetails = async (request_number: string) => {
    try {
      dispatch(actionSetRequestDetailsLoading());
      const lsUserData = JSON.parse(
        LocalStorage.get(USER_DATA_NAMESPACE) || ""
      );

      const url = `${API_BASE_URL}${API_ENDPOINTS.DETAILS}`;
      const res: RequestDetailsEntityRes = await Http.post(
        url,
        {},
        {
          request_number,
          lang: layoutState.languageTrans,
          sa_cc: UtilsLocale.convertISO3toISO2(lsUserData?.countryCode),
          site_cc: layoutState.countryCode,
        }
      );
      dispatch(actionSetRequestDetails(res));
    } catch (e: any) {
      console.error(e);

      // temporary fix, should implement api error handler
      if (e.rcode && e.rcode === RCODES.INVALID_REQUEST_NUMBER) {
        window.location.href = ROUTES.ERROR_400;
      }
    }
  };

  const sendFilePassword = async (request_number: string) => {
    try {
      triggerGAEvent(
        EVENT_CATEGORY_REQUEST_DETAILS,
        EVENT_ACTION_DETAILS_SEND_FILE_PASSWORD
      );
      const url = `${API_BASE_URL}${API_ENDPOINTS.SEND_FILE_PASSWORD}`;
      const res: BaseEntityRes = await Http.post(
        url,
        {},
        {
          request_number,
        }
      );

      if (res.rcode === 0) {
        dispatch(actionSetFilePasswordSend(true));
      }
    } catch (e: any) {
      console.error(e);
    }
  };

  const closeFilePasswordModal = () => {
    dispatch(actionSetFilePasswordSend(false));
  };

  const cancelRequest = async (request_number: string) => {
    try {
      dispatch(actionSetCancelRequestPending(true));
      triggerGAEvent(
        EVENT_CATEGORY_REQUEST_DETAILS,
        EVENT_ACTION_DETAILS_CANCEL_REQUEST
      );
      const url = `${API_BASE_URL}${API_ENDPOINTS.CANCEL_REQUEST}`;
      const res: BaseEntityRes = await Http.post(
        url,
        {},
        {
          request_number,
        }
      );

      dispatch(actionSetCancelRequestPending(false));

      if (res.rcode === 0) {
        dispatch(actionSetCancelRequestComplete(true));
      }
    } catch (e: any) {
      console.error(e);
    }
  };

  const closCancelRequestModal = () => {
    dispatch(actionSetCancelRequestComplete(false));
  };

  const handleGAEvent = (request_type: string, request_status?: string) => {
    const requestStatus = request_status ? request_status : "L";
    switch (request_type) {
      case HISTORY_REQUEST_TYPE.DOWNLOAD:
        triggerGAEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_DOWNLOAD[requestStatus]
        );
        break;
      case HISTORY_REQUEST_TYPE.DELETE:
        triggerGAEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_DELETION[requestStatus]
        );
        break;
      case HISTORY_REQUEST_TYPE.UNSUBSCRIBE:
        triggerGAEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_OPTOUT[requestStatus]
        );
        break;
      case HISTORY_REQUEST_TYPE.ALL:
        triggerGAEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_ALL[requestStatus]
        );
        break;
    }
  };

  const loadMyDataHistory = async (
    body: MyDataHistoryReqBody,
    isNextPage: boolean = false
  ) => {
    try {
      dispatch(actionSetHasMoreHistory(false));
      dispatch(actionSetMyDataHistoryLoading());
      dispatch(actionSetHistoryMoreLoading(true));

      handleGAEvent(body.request_type, body.request_status);
      if (!body.request_status) {
        delete body.request_status;
      }

      const lsUserData = JSON.parse(
        LocalStorage.get(USER_DATA_NAMESPACE) || ""
      );
      const saCountryCode =
        userState.userData.countryCode || lsUserData?.countryCode;
      if (!isNextPage) {
        dispatch(actionSetMyDataHistory(myDataHistoryDefaults));
        body = {
          ...body,
          offset: 0,
          lang: layoutState.languageTrans,
          sa_cc: UtilsLocale.convertISO3toISO2(saCountryCode),
          site_cc: layoutState.countryCode,
        };
      }

      dispatch(actionSetHistoryReqBody(body));
      const url = `${API_BASE_URL}${API_ENDPOINTS.HISTORY}`;
      const res: MyDataHistoryEntity = await Http.post(url, body);

      if (isNextPage) {
        dispatch(actionSetMyDataLoadMoreHistory(res));
      } else {
        dispatch(actionSetMyDataHistory(res));
      }

      dispatch(actionSetHistoryMoreLoading(false));

      if (res.next_offset && res.next_offset > 0) {
        dispatch(actionSetHasMoreHistory(true));
      } else {
        dispatch(actionSetHasMoreHistory(false));
      }
    } catch (e: any) {
      console.error(e);
    }
  };

  const closeGlobalSiteLinkPopup = () => {
    dispatch(actionSetGlobalSiteLinkPopupClose());
  };

  const resetRequestBody = () => {
    dispatch(actionSetHistoryReqBody(myDataHistoryReqBodyDefaults));
  };

  return {
    loadSummary,
    loadRequestDetails,
    loadMyDataHistory,
    sendFilePassword,
    closeFilePasswordModal,
    cancelRequest,
    closCancelRequestModal,
    confirmRequest,
    clearConfirmRequestData,
    closeGlobalSiteLinkPopup,
    resetRequestBody,
  };
};
